/**
 * TrackingService - Singleton service managing the GPS recording lifecycle.
 *
 * State machine: IDLE -> RECORDING -> PAUSED -> (RECORDING) -> STOPPED -> PROCESSING -> COMPLETE
 *
 * Uses an event emitter pattern so UI components can subscribe to GPS updates
 * without tight coupling. In web builds, falls back to browser Geolocation API.
 * In native (Capacitor) builds, the Capacitor Background Geolocation plugin
 * would be used instead (marked with TODO comments below).
 *
 * @typedef {import('../types/index.js').GpsPoint} GpsPoint
 * @typedef {import('../types/index.js').Session} Session
 */

import { v4 as uuidv4 } from 'uuid';
import { registerPlugin } from '@capacitor/core';
import { processTrack } from './GpsProcessor.js';
import { classifyTrack, classifyCurrentActivity } from './SegmentClassifier.js';
import { calculateSessionStats, calculateLiveStats } from './StatsCalculator.js';
import { detectResort } from './ResortDetector.js';
import { saveActiveSession, clearActiveSession } from '../services/db/meta.js';

function isNativeCapacitor() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
}

// @capacitor-community/background-geolocation ships as a native-only
// plugin (no JS runtime). registerPlugin returns a proxy that dispatches
// through Capacitor.Plugins on native and is a no-op on web.
/** @type {import('@capacitor-community/background-geolocation').BackgroundGeolocationPlugin} */
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

// ─── Recording states ────────────────────────────────────────────────────────
/** @enum {string} */
export const RecordingState = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PAUSED: 'paused',
  STOPPED: 'stopped',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
};

// ─── Event names ─────────────────────────────────────────────────────────────
export const TrackingEvents = {
  STATE_CHANGE: 'stateChange',
  GPS_POINT: 'gpsPoint',
  LIVE_STATS: 'liveStats',
  ACTIVITY_CHANGE: 'activityChange',
  SESSION_COMPLETE: 'sessionComplete',
  ERROR: 'error',
};

// GPS polling interval for web fallback (ms)
const WEB_GPS_INTERVAL = 3000;

// How often to recalculate live stats (ms)
const LIVE_STATS_INTERVAL = 5000;

// Number of recent points used for activity classification
const ACTIVITY_WINDOW = 10;

class TrackingService {
  constructor() {
    /** @type {RecordingState} */
    this._state = RecordingState.IDLE;

    /** @type {GpsPoint[]} */
    this._rawPoints = [];

    /** @type {string | null} */
    this._sessionId = null;

    /** @type {number} */
    this._startTime = 0;

    /** @type {number} */
    this._pauseAccumulator = 0; // total ms spent paused

    /** @type {number | null} */
    this._pauseStart = null;

    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();

    /** @type {number | null} - ID returned by setInterval for web GPS polling */
    this._gpsIntervalId = null;

    /** @type {number | null} - ID returned by setInterval for live stats */
    this._statsIntervalId = null;

    /** @type {number | null} - Browser watchPosition ID */
    this._watchId = null;

    /** @type {string | null} - Capacitor BackgroundGeolocation watcher ID */
    this._bgWatcherId = null;

    /** @type {string | null} - resort context (used by snapshot only) */
    this._resortId = null;
    /** @type {string | null} */
    this._resortName = null;
    /** @type {number} - GPS points written to disk since last snapshot save */
    this._pointsSinceSnapshot = 0;
  }

  /**
   * Build a serialisable snapshot of the current recording state.
   * @returns {Object | null}
   */
  _snapshot() {
    if (this._state === RecordingState.IDLE || this._state === RecordingState.COMPLETE) {
      return null;
    }
    return {
      state: this._state,
      sessionId: this._sessionId,
      startTime: this._startTime,
      pauseAccumulator: this._pauseAccumulator,
      pauseStart: this._pauseStart,
      rawPoints: this._rawPoints,
      resortId: this._resortId,
      resortName: this._resortName,
      savedAt: Date.now(),
    };
  }

  /**
   * Persist the current snapshot to Dexie meta. Fire-and-forget; errors are
   * logged. Throttles: GPS-point saves run every 5 points; state transitions
   * always save immediately.
   * @param {boolean} [force]
   */
  async _persist(force = false) {
    try {
      if (force) this._pointsSinceSnapshot = 0;
      const snap = this._snapshot();
      if (snap) {
        await saveActiveSession(snap);
      }
    } catch (err) {
      console.warn('[TrackingService] persist failed:', err?.message || err);
    }
  }

  /**
   * Rehydrate from a previously-saved snapshot. Used at app boot to resume an
   * in-progress session after the app was killed. Restarts GPS tracking and
   * live-stats timers if state was RECORDING.
   * @param {Object} snapshot
   */
  restoreFromSnapshot(snapshot) {
    if (!snapshot || !snapshot.sessionId) return false;
    this._sessionId = snapshot.sessionId;
    this._startTime = snapshot.startTime || 0;
    this._pauseAccumulator = snapshot.pauseAccumulator || 0;
    this._pauseStart = snapshot.pauseStart || null;
    this._rawPoints = Array.isArray(snapshot.rawPoints) ? snapshot.rawPoints : [];
    this._resortId = snapshot.resortId || null;
    this._resortName = snapshot.resortName || null;
    this._state = snapshot.state || RecordingState.PAUSED;
    this._emit(TrackingEvents.STATE_CHANGE, { from: RecordingState.IDLE, to: this._state });
    if (this._state === RecordingState.RECORDING) {
      this._startGpsTracking();
      this._startLiveStats();
    }
    return true;
  }

  // ─── Event Emitter ───────────────────────────────────────────────────────

  /**
   * Subscribe to a tracking event.
   * @param {string} event
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const set = this._listeners.get(event);
      if (set) set.delete(callback);
    };
  }

  /**
   * Emit an event to all subscribers.
   * @param {string} event
   * @param {*} data
   */
  _emit(event, data) {
    const set = this._listeners.get(event);
    if (set) {
      for (const cb of set) {
        try {
          cb(data);
        } catch (err) {
          console.error(`[TrackingService] Listener error for "${event}":`, err);
        }
      }
    }
  }

  // ─── State Management ────────────────────────────────────────────────────

  /** @returns {RecordingState} */
  get state() {
    return this._state;
  }

  /** @returns {GpsPoint[]} */
  get rawPoints() {
    return this._rawPoints;
  }

  /** @returns {string | null} */
  get sessionId() {
    return this._sessionId;
  }

  /**
   * Elapsed recording time in milliseconds, excluding paused time.
   *
   * Note: we rely on `Date.now()` because the elapsed time must survive
   * the JS context being killed and rehydrated from a Dexie snapshot
   * (where `performance.now()` resets to 0). To defend against a
   * device-clock rollback (e.g. NTP correction, time-zone change), we
   * clamp negative deltas to 0 instead of letting the UI display a
   * negative timer.
   * @returns {number}
   */
  get elapsedTime() {
    if (this._startTime === 0) return 0;
    const now = this._state === RecordingState.PAUSED ? this._pauseStart : Date.now();
    const raw = now - this._startTime - this._pauseAccumulator;
    return raw > 0 ? raw : 0;
  }

  /**
   * Transition to a new state with validation.
   * @param {RecordingState} newState
   */
  _setState(newState) {
    const old = this._state;
    this._state = newState;
    this._emit(TrackingEvents.STATE_CHANGE, { from: old, to: newState });
    // Persist on every state change
    this._persist(true);
  }

  // ─── Recording Lifecycle ─────────────────────────────────────────────────

  /**
   * Start a new recording session.
   * Initializes GPS tracking using the web Geolocation API.
   */
  startRecording(resortId, resortName) {
    if (this._state !== RecordingState.IDLE && this._state !== RecordingState.COMPLETE) {
      console.warn(`[TrackingService] Cannot start from state "${this._state}"`);
      return;
    }

    // Reset internal state
    this._rawPoints = [];
    this._sessionId = uuidv4();
    this._startTime = Date.now();
    this._pauseAccumulator = 0;
    this._pauseStart = null;
    this._resortId = resortId || null;
    this._resortName = resortName || null;
    this._pointsSinceSnapshot = 0;

    this._setState(RecordingState.RECORDING);

    // Start GPS tracking
    this._startGpsTracking();

    // Start live stats computation
    this._startLiveStats();
  }

  /**
   * Pause the current recording. GPS tracking stops to save battery.
   */
  pauseRecording() {
    if (this._state !== RecordingState.RECORDING) {
      console.warn(`[TrackingService] Cannot pause from state "${this._state}"`);
      return;
    }

    this._pauseStart = Date.now();
    this._stopGpsTracking();
    this._stopLiveStats();
    this._setState(RecordingState.PAUSED);
  }

  /**
   * Resume a paused recording.
   */
  resumeRecording() {
    if (this._state !== RecordingState.PAUSED) {
      console.warn(`[TrackingService] Cannot resume from state "${this._state}"`);
      return;
    }

    // Accumulate paused time
    if (this._pauseStart) {
      this._pauseAccumulator += Date.now() - this._pauseStart;
      this._pauseStart = null;
    }

    this._setState(RecordingState.RECORDING);
    this._startGpsTracking();
    this._startLiveStats();
  }

  /**
   * Stop the recording and trigger processing.
   * @returns {Promise<Session>} The completed session object
   */
  async stopRecording() {
    if (
      this._state !== RecordingState.RECORDING &&
      this._state !== RecordingState.PAUSED
    ) {
      console.warn(`[TrackingService] Cannot stop from state "${this._state}"`);
      return null;
    }

    // If paused, account for final pause duration
    if (this._pauseStart) {
      this._pauseAccumulator += Date.now() - this._pauseStart;
      this._pauseStart = null;
    }

    this._stopGpsTracking();
    this._stopLiveStats();
    this._setState(RecordingState.STOPPED);

    // Process the recorded data
    return this._processSession();
  }

  /**
   * Discard the current session without processing.
   */
  discardRecording() {
    this._stopGpsTracking();
    this._stopLiveStats();
    this._rawPoints = [];
    this._sessionId = null;
    this._startTime = 0;
    this._pauseAccumulator = 0;
    this._pauseStart = null;
    clearActiveSession().catch(() => {});
    this._setState(RecordingState.IDLE);
  }

  // ─── GPS Tracking ───────────────────────────────────────────────────────

  /**
   * Start collecting GPS points.
   *
   * Native (Capacitor): uses `@capacitor-community/background-geolocation`
   * so points keep flowing while the app is backgrounded. The
   * `backgroundMessage` option is what tells iOS to continue delivering
   * updates once the app leaves the foreground; without it, the plugin
   * would silently degrade to foreground-only.
   *
   * Web: falls back to `navigator.geolocation.watchPosition` + a polling
   * interval (browser watchPosition frequency is inconsistent).
   */
  _startGpsTracking() {
    if (isNativeCapacitor()) {
      BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'PowderLog is tracking your ski runs.',
          backgroundTitle: 'PowderLog',
          requestPermissions: true,
          stale: false,
          distanceFilter: 5,
        },
        (location, error) => {
          if (error) {
            // NOT_AUTHORIZED — the user denied "Always" permission.
            if (error.code === 'NOT_AUTHORIZED') {
              this._emit(TrackingEvents.ERROR, {
                code: 'GPS_PERMISSION_DENIED',
                message: 'Location permission denied. Enable "Always" in Settings to record while the app is in the background.',
              });
            } else {
              this._emit(TrackingEvents.ERROR, {
                code: `GPS_ERROR_${error.code || 'UNKNOWN'}`,
                message: error.message || String(error),
              });
            }
            return;
          }
          if (!location) return;
          this._handleNativeLocation(location);
        },
      )
        .then((id) => {
          this._bgWatcherId = id;
        })
        .catch((err) => {
          console.warn('[TrackingService] BackgroundGeolocation.addWatcher failed:', err?.message || err);
          // Fall back to web geolocation so we don't just stop recording.
          this._startWebGeolocation();
        });
      return;
    }

    this._startWebGeolocation();
  }

  _startWebGeolocation() {
    if (!navigator.geolocation) {
      this._emit(TrackingEvents.ERROR, {
        code: 'GEOLOCATION_UNAVAILABLE',
        message: 'Geolocation API is not available in this browser.',
      });
      return;
    }

    this._watchId = navigator.geolocation.watchPosition(
      (position) => this._handlePosition(position),
      (error) => this._handleGpsError(error),
      {
        enableHighAccuracy: true,
        maximumAge: 2000,
        timeout: 10000,
      }
    );

    // Additionally poll at a fixed interval to ensure we get regular readings,
    // since watchPosition frequency is browser-dependent.
    this._gpsIntervalId = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => this._handlePosition(position),
        () => {
          // Silently ignore polling errors; watchPosition is the primary source
        },
        {
          enableHighAccuracy: true,
          maximumAge: 1000,
          timeout: 5000,
        }
      );
    }, WEB_GPS_INTERVAL);
  }

  /**
   * Stop GPS tracking (both native and web paths).
   */
  _stopGpsTracking() {
    if (this._watchId != null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }
    if (this._gpsIntervalId != null) {
      clearInterval(this._gpsIntervalId);
      this._gpsIntervalId = null;
    }

    if (this._bgWatcherId != null) {
      const id = this._bgWatcherId;
      this._bgWatcherId = null;
      BackgroundGeolocation.removeWatcher({ id }).catch((err) =>
        console.warn('[TrackingService] removeWatcher failed:', err?.message || err),
      );
    }
  }

  /**
   * Convert a Capacitor BackgroundGeolocation `Location` into our GpsPoint
   * shape and delegate to the shared handler.
   * @param {{
   *   latitude: number, longitude: number, altitude: number|null,
   *   speed: number|null, accuracy: number, bearing: number|null,
   *   time: number|null,
   * }} loc
   */
  _handleNativeLocation(loc) {
    if (this._state !== RecordingState.RECORDING) return;
    this._handlePosition({
      coords: {
        latitude: loc.latitude,
        longitude: loc.longitude,
        altitude: loc.altitude,
        speed: loc.speed,
        accuracy: loc.accuracy,
        heading: loc.bearing,
      },
      timestamp: loc.time || Date.now(),
    });
  }

  /**
   * Handle a raw browser GeolocationPosition.
   * @param {GeolocationPosition} position
   */
  _handlePosition(position) {
    if (this._state !== RecordingState.RECORDING) return;

    const { coords, timestamp } = position;

    /** @type {GpsPoint} */
    const point = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      altitude: coords.altitude ?? 0,
      speed: coords.speed ?? 0,
      timestamp: timestamp,
      accuracy: coords.accuracy ?? 999,
      heading: coords.heading ?? undefined,
    };

    // De-duplicate: skip if the point has the same timestamp as the last one
    const lastPoint = this._rawPoints[this._rawPoints.length - 1];
    if (lastPoint && lastPoint.timestamp === point.timestamp) return;

    this._rawPoints.push(point);
    this._emit(TrackingEvents.GPS_POINT, point);

    // Classify current activity from recent points
    const recent = this._rawPoints.slice(-ACTIVITY_WINDOW);
    const activity = classifyCurrentActivity(recent);
    this._emit(TrackingEvents.ACTIVITY_CHANGE, activity);

    // Persist every 5 GPS points to bound disk writes
    this._pointsSinceSnapshot += 1;
    if (this._pointsSinceSnapshot >= 5) {
      this._persist(true);
    }
  }

  /**
   * Handle GPS error.
   * @param {GeolocationPositionError} error
   */
  _handleGpsError(error) {
    this._emit(TrackingEvents.ERROR, {
      code: `GPS_ERROR_${error.code}`,
      message: error.message,
    });
  }

  // ─── Live Stats ──────────────────────────────────────────────────────────

  _startLiveStats() {
    this._statsIntervalId = setInterval(() => {
      if (this._state !== RecordingState.RECORDING) return;
      const stats = calculateLiveStats(this._rawPoints);
      this._emit(TrackingEvents.LIVE_STATS, stats);
    }, LIVE_STATS_INTERVAL);
  }

  _stopLiveStats() {
    if (this._statsIntervalId != null) {
      clearInterval(this._statsIntervalId);
      this._statsIntervalId = null;
    }
  }

  // ─── Session Processing ──────────────────────────────────────────────────

  /**
   * Run the full processing pipeline on recorded data.
   * @returns {Promise<Session>}
   */
  async _processSession() {
    this._setState(RecordingState.PROCESSING);

    try {
      // 1. Process raw GPS points (filter, smooth, enrich)
      const processedPoints = processTrack(this._rawPoints);

      // 2. Classify into segments
      const segments = classifyTrack(processedPoints);

      // 3. Calculate session statistics
      const stats = calculateSessionStats(segments, processedPoints);

      // 4. Detect resort from the first valid point — but ONLY if the
      //    caller didn't already pass one to startRecording(). The GPS
      //    detector uses a coarse 5 km radius and was silently overwriting
      //    the user's explicit choice.
      let resortId = this._resortId || null;
      let resortName = this._resortName || null;
      if (!resortId && processedPoints.length > 0) {
        const firstPoint = processedPoints[0];
        const resort = detectResort(firstPoint.latitude, firstPoint.longitude);
        if (resort) {
          resortId = resort.id;
          resortName = resort.name;
        }
      }

      const now = new Date().toISOString();

      /** @type {Session} */
      const session = {
        id: this._sessionId,
        userId: '', // will be set by the store/caller
        resortId,
        resortName,
        status: 'completed',
        startTime: this._startTime,
        endTime: Date.now(),
        segments,
        stats,
        notes: null,
        photos: [],
        conditions: null,
        createdAt: now,
        updatedAt: now,
      };

      this._setState(RecordingState.COMPLETE);
      this._emit(TrackingEvents.SESSION_COMPLETE, session);
      // Snapshot is no longer needed; the completed session is persisted by
      // the caller via the sessions repo.
      clearActiveSession().catch(() => {});

      return session;
    } catch (error) {
      this._emit(TrackingEvents.ERROR, {
        code: 'PROCESSING_ERROR',
        message: error.message,
      });
      this._setState(RecordingState.IDLE);
      return null;
    }
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────────

  /**
   * Full cleanup. Call when the service is no longer needed.
   */
  destroy() {
    this._stopGpsTracking();
    this._stopLiveStats();
    this._listeners.clear();
    this._rawPoints = [];
  }
}

// ─── Singleton ───────────────────────────────────────────────────────────────

let _instance = null;

/**
 * Get the singleton TrackingService instance.
 * @returns {TrackingService}
 */
export function getTrackingService() {
  if (!_instance) {
    _instance = new TrackingService();
  }
  return _instance;
}

/**
 * Reset the singleton (useful for testing).
 */
export function resetTrackingService() {
  if (_instance) {
    _instance.destroy();
    _instance = null;
  }
}

export default TrackingService;
